"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Flame, Loader as Loader2, Mail, MapPin, Phone, ShieldCheck } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const supportHighlights = [{
  icon: ShieldCheck,
  title: "Regulated Laboratory & Field Testing",
  description: "Certified analytical specialists for pharmaceutical, chemical, and field HazMat operations across Ireland & the UK."
}, {
  icon: Flame,
  title: "Rapid Response & Feasibility Studies",
  description: "Direct access to application engineers for handheld Raman, NIR, and trace explosive/narcotic detection."
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-muted/40 rounded-xl border border-border">
        <div className="p-3 bg-primary/10 text-primary rounded-full mb-4">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Message Received</h3>
        <p className="text-sm text-muted-foreground mb-6 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-md">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Name *</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="work_email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Work Email *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel>Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 20 1234 5678" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Please enter at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message *</FormLabel>
            <FormControl>
              <Textarea rows={4} placeholder="Your message..." className="resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-11 text-base font-medium rounded-md shadow-sm mt-2 cursor-pointer">
        {isSubmitting ? <>
            <Loader2 className="animate-spin size-4 mr-2" />
            Sending Request...
          </> : "Send Inquiry"}
      </Button>
    </Form>;
}
export default function Contact() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="contact" data-nav="light" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column (5 Cols) */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col justify-between h-full space-y-8">
            <div>
              <Badge variant="secondary" className="mb-4 px-3 py-1 font-semibold tracking-wide uppercase text-xs">
                Application Support
              </Badge>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight mb-6">
                Discuss Your Fire & HazMat Application
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-8">
                Connect directly with our analytical specialists for equipment evaluation, method feasibility, and regulatory qualification across Ireland and the UK.
              </p>

              <div className="space-y-6 pt-2 border-t border-border">
                {supportHighlights.map((item, index) => {
                const Icon = item.icon;
                return <div key={index} data-index={index} className="flex items-start gap-4">
                      <div className="p-2.5 rounded-lg bg-secondary text-primary shrink-0 mt-0.5">
                        <Icon className="size-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-foreground mb-1">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
            </div>

            <div className="p-6 bg-secondary/50 rounded-xl border border-border space-y-3">
              <div className="flex items-center gap-3 text-sm text-foreground">
                <MapPin className="size-4 text-primary shrink-0" />
                <span>Ireland & United Kingdom Technical Support</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-foreground">
                <Mail className="size-4 text-primary shrink-0" />
                <span>Direct application and field engineering inquiries</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-foreground">
                <Phone className="size-4 text-primary shrink-0" />
                <span>Rapid response qualification consultation</span>
              </div>
            </div>
          </motion.div>

          {/* Right Column (7 Cols) */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-7">
            <Card className="rounded-2xl border border-border shadow-lg bg-card text-card-foreground p-2 sm:p-4">
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl font-bold font-default text-foreground">
                  Contact Us - Antech Solutions
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  Complete the technical intake form below and an analytical specialist will respond promptly.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formId="contact_form" sectionName="contact-section" defaultValues={{
                name: "",
                work_email: "",
                company_name: "",
                phone_number: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                work_email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Send Inquiry" successMessage="Thank you for reaching out. Our team will review your application specifications and contact you shortly.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}